"use client"

import { fetchCurrentClientAuthSession, isClientAuthSessionLost, shouldCheckClientAuthSession, shouldCheckClientAuthSessionCookieEvent } from "@/app/(normal)/clientAuthSession"
import type { TLoginUserData } from "@/schemas/user.schema"
import { useAppStoreContext } from "@/stores/appStore"
import { hydrateSyncStoreFromInitialUserData, initializeTwilioSyncClient } from "@/sync/sync"
import { useEmbeddedEvents } from "@/hooks/useEmbeddedEvents"
import { useIntercom } from "@/hooks/useIntercom"
import { usePathname } from "next/navigation"
import { useFullStory } from "@/hooks/useFullStory"
import { useLogout } from "@/hooks/useLogout"
import { useSelector } from "@legendapp/state/react"
import { syncStore$ } from "@/stores/sync.v2.store"
import { buildFrontUrl } from "@/lib/front-url"
import { getCookieStoreEventTarget } from "@/lib/clientCookieStore"
import React, { useEffect, useLayoutEffect, useRef } from "react"

const CLIENT_AUTH_SESSION_CHECK_MIN_INTERVAL_MS = 15_000

interface ClientRuntimeEffectsProps {
  userData?: TLoginUserData
  intercomUserHash?: string
}

export function ClientRuntimeEffects({ userData, intercomUserHash }: ClientRuntimeEffectsProps) {
  const pathname = usePathname()
  const { setUserData, changeSidebarMenuState } = useAppStoreContext((s) => s)
  const { isReady: isFullStoryReady, setIdentity } = useFullStory()
  const { isReady: isIntercomReady, initialize, shutdown } = useIntercom()
  const { performLogout } = useLogout()
  const lastUserID = useRef<number | null | undefined>(null)
  const lastClusterID = useRef<string | undefined>(undefined)
  const lastAuthSessionCheckAt = useRef(0)
  const clusterID = useSelector(() => syncStore$.account.clusterId.get())

  useEmbeddedEvents()

  useLayoutEffect(() => {
    if (!userData || userData.account.status === "LOGGED_OUT") {
      return
    }

    setUserData(userData)
    hydrateSyncStoreFromInitialUserData(userData)
    lastClusterID.current = userData.account.clusterId
  }, [setUserData, userData])

  useEffect(() => {
    changeSidebarMenuState(false)

    let checkInFlight = false

    const checkAuthSession = async ({ force = false }: { force?: boolean } = {}) => {
      if (checkInFlight || !shouldCheckClientAuthSession(pathname)) {
        return
      }

      const now = Date.now()
      if (!force && now - lastAuthSessionCheckAt.current < CLIENT_AUTH_SESSION_CHECK_MIN_INTERVAL_MS) {
        return
      }

      lastAuthSessionCheckAt.current = now
      checkInFlight = true

      try {
        const isAuthenticated = await fetchCurrentClientAuthSession()

        if (!isClientAuthSessionLost(isAuthenticated)) {
          return
        }

        const loginFullURL = buildFrontUrl("/login", {
          frontUrl: process.env.NEXT_PUBLIC_FRONT_URL,
          fallbackOrigin: window.location.origin,
        })
        window.location.replace(loginFullURL)
      } finally {
        checkInFlight = false
      }
    }

    const requestAuthSessionCheck = ({ force = false }: { force?: boolean } = {}) => {
      void checkAuthSession({ force })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        return
      }

      requestAuthSessionCheck()
    }

    const handleCookieChange = (event: Event) => {
      if (!shouldCheckClientAuthSessionCookieEvent(event)) {
        return
      }

      requestAuthSessionCheck({ force: true })
    }

    const handleNavigationResume = () => {
      requestAuthSessionCheck()
    }

    requestAuthSessionCheck({ force: true })

    const cookieStore = getCookieStoreEventTarget()
    window.addEventListener("focus", handleNavigationResume)
    window.addEventListener("pageshow", handleNavigationResume)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    cookieStore?.addEventListener("change", handleCookieChange)

    return () => {
      window.removeEventListener("focus", handleNavigationResume)
      window.removeEventListener("pageshow", handleNavigationResume)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      cookieStore?.removeEventListener("change", handleCookieChange)
    }
  }, [changeSidebarMenuState, pathname])

  useEffect(() => {
    if (!isFullStoryReady) {
      return
    }

    if (userData && userData.account.status !== "LOGGED_OUT") {
      setIdentity({
        uid: userData.user.id.toString(),
        properties: {
          displayName: `${userData.user.name} ${userData.user.lastName}`,
          email: userData.user.email,
          companyName: userData.user.companyName,
          account: userData.user.accountId,
          status: userData.user.accountState,
          seats: userData.user.accountSeats,
          signupChannel: userData.user.accountSignupChannel,
          signupPlan: userData.user.accountSignupPlan,
          smsOptIn: userData.user.smsOptIn,
          emailMarketingOptIn: userData.user.emailMarketingOptIn,
        },
      })
      return
    }

    setIdentity({
      anonymous: true,
    })
  }, [isFullStoryReady, setIdentity, userData])

  useEffect(() => {
    if (!userData) {
      return
    }

    if (userData.user.id === lastUserID.current) {
      return
    }

    lastUserID.current = userData.user.id

    if (userData.account.status !== "LOGGED_OUT") {
      void initializeTwilioSyncClient({
        initialUserData: userData,
        token: userData.twilio.syncToken,
      }).catch(() => {})

      if (!isIntercomReady) {
        initialize({
          userData,
          userHash: intercomUserHash,
        })
      }

      return
    }

    if (isIntercomReady) {
      shutdown()
    }
  }, [initialize, intercomUserHash, isIntercomReady, shutdown, userData])

  useEffect(() => {
    if (!clusterID || !lastClusterID.current) {
      lastClusterID.current = clusterID || undefined
      return
    }

    if (clusterID !== lastClusterID.current) {
      performLogout()
      return
    }

    lastClusterID.current = clusterID
  }, [clusterID, performLogout])

  return null
}
